import React from 'react';
import styled from 'styled-components';
import Icon from '../../../../../../common/Icon';
import useEstimatedDeliveryDate from '../../../../../../hooks/useEstimatedDeliveryDate';
import { BodyFour } from '../../../../../../styles/theme/default/typography';
import ShippingInformation from '../../shippingInformation/ShippingInformation';
import AddToCartButton from './AddToCartButton';
import InventoryLeft from './InventoryLeft';
import WaitlistButton from './WaitlistButton';

const Wrapper = styled.div`
  margin-top: ${({ isWallpaper }) => (isWallpaper ? '15px' : '35px')};
  padding: ${({ sourceWallpaperSideBar }) =>
    sourceWallpaperSideBar ? '0' : '0 25px'};
  margin-bottom: ${({ isPurchasable }) => (isPurchasable ? '0' : '25px')};

  .text {
    background: #f7f9fa;
    padding: 10px;
  }
  .cart-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .discount-not-apply-copy {
    margin-top: 10px;
  }

  .est-flag {
    text-align: center;
    color: ${({ theme }) => theme.colors.french};
    font-weight: bold;
    padding: 20px 0;
    margin-top: 20px;
    background: #f7f9fa;
  }

  .qty-minus,
  .qty-plus {
    cursor: pointer;
  }

  .qty-plus {
    opacity: ${({ disablePlusQuantity }) => (disablePlusQuantity ? 0.5 : 1)};
  }

  .qty-minus {
    opacity: ${({ cartQuantity }) => (cartQuantity === 1 ? 0.5 : 1)};
  }

  .est-date {
    text-align: center;
    margin-top: 18px;
    margin-bottom: 20px;

    background: ${({ theme }) => theme.old ? theme.colors.oatmilk : theme.colors.lightOatmilk};
    padding: 15px 0;

    .truck-icon {
      margin-right: 10px;
    }
  }

  .bedroom-sets-note {
    margin-top: 12px;
  }

  .inventory-left {
    margin-bottom: 25px;
  }

  .shipping-info-container {
    margin-bottom: 25px;
  }

  .bundle-individual {
    padding: 15px;
    display: flex;
  }

  .delivery-copy {
    text-align: initial;
    margin-left: 10px;
  }

  .truck-icon-container {
    width: 30px;
  }

  .email-link {
    color: ${({ theme }) => theme.colors.french};
  }
`;

function Cart({
  handleToggleSideCart,
  productData,
  isPurchasable,
  isMobile,
  cartQuantity,
  setCartQuantity,
  isWallpaper,
  rolls,
  sourceWallpaperSideBar,
  extendPlanSelected
}) {
  const { estimatedStartDate, estimatedEndDate} = useEstimatedDeliveryDate(productData);

  const usesInventory = !!productData?.uses_inventory;
  const currentInventory = productData?.current_inventory;
  const disablePlusQuantity =
    usesInventory && !isWallpaper && cartQuantity >= currentInventory;

  const allowIndividualPurchases = productData?.allow_individual_purchases;
  const isBedroomSets =
    productData?.frame?.primarySubcategory.category_id == 39;

  return (
    <Wrapper
      sourceWallpaperSideBar={sourceWallpaperSideBar}
      isWallpaper={isWallpaper}
      cartQuantity={cartQuantity}
      isPurchasable={isPurchasable}
      disablePlusQuantity={disablePlusQuantity}
    >
      <InventoryLeft product={productData} />

      {!isPurchasable ? (
        <div className="text">
          This product is currently sold out. Get added to the waitlist and
          we'll let you know when it is available.
        </div>
      ) : (
        ''
      )}

      <div className="cart-container">
        {isPurchasable ? (
          <AddToCartButton
            rolls={rolls}
            sourceWallpaperSideBar={sourceWallpaperSideBar}
            isWallpaper={isWallpaper}
            productData={productData}
            cartQuantity={cartQuantity}
            isPurchasable={isPurchasable}
            handleToggleSideCart={handleToggleSideCart}
            isMobile={isMobile}
            extendPlanSelected={extendPlanSelected}
          />
        ) : (
          <WaitlistButton productId={productData.id} />
        )}

        {!isWallpaper && !allowIndividualPurchases && (
          <Quantity>
            <div
              className="qty-minus"
              onClick={() => {
                if (cartQuantity > 1) {
                  setCartQuantity(cartQuantity - 1);
                }
              }}
            >
              <img src="/assets/pdp/qty-minus.svg" alt="add one less to cart" />
            </div>
            <span className="qty-number">{cartQuantity}</span>
            <div
              className="qty-plus"
              onClick={() => {
                if (disablePlusQuantity) return;
                setCartQuantity(cartQuantity + 1);
              }}
            >
              <img src="/assets/pdp/qty-plus.svg" alt="add one more to cart" />
            </div>
          </Quantity>
        )}
      </div>

      {productData.price === productData.minimum_advertised_price && (
        <BodyFour className="discount-not-apply-copy" color="#6C6C6C">
          *Discount codes do not apply to this product.
        </BodyFour>
      )}

      {isBedroomSets && (
        <BodyFour className="bedroom-sets-note">
          *Please note that you'll be able to edit all items in cart
        </BodyFour>
      )}

      {isPurchasable ? (
        <div className="estimated-delivery-date">
          {allowIndividualPurchases ? (
            <BodyFour className="est-date bundle-individual">
              <div className="truck-icon-container">
                <Icon
                  className="truck-icon"
                  icon="ti-truck"
                  size={30}
                  color="#2A5CAF"
                />{' '}
              </div>
              <div className="delivery-copy">
                This set includes items made in multiple factories. Items will
                arrive between {estimatedStartDate} and {estimatedEndDate}. If you
                have any questions, please contact{' '}
                <a className="email-link" href="hi@theinside.com">
                  hi@theinside.com
                </a>
                .
              </div>
            </BodyFour>
          ) : (
            <BodyFour className="est-date">
              <Icon
                className="truck-icon"
                icon="ti-truck"
                size={30}
                color="#2A5CAF"
              />{' '}
              Estimated Delivery Date: {estimatedStartDate} - {estimatedEndDate}
            </BodyFour>
          )}

          {!isWallpaper && (
            <div className="shipping-info-container">
              <ShippingInformation />
            </div>
          )}
        </div>
      ) : (
        ''
      )}
    </Wrapper>
  );
}

export default Cart;

const Quantity = styled.div`
  display: flex;
  .qty-number {
    width: 12px;
    margin: 0 20px;
  }
`;
